import { z } from 'zod';
export const Word = z.object({
    start: z.number().min(0),
    end: z.number().min(0),
    text: z.string(),
});
export const Segment = z.object({
    id: z.number().int(),
    start: z.number().min(0),
    end: z.number().min(0),
    text: z.string(),
    speaker: z.string().nullable().default(null),
    words: z.array(Word).default([]),
});
export const Transcript = z.object({
    language: z.string().max(16).default('de'),
    /** Seconds to add to every timestamp, so a clip transcript maps onto the sequence. */
    offset: z.number().default(0),
    segments: z.array(Segment).default([]),
});
function pad(n, len) {
    return String(Math.floor(n)).padStart(len, '0');
}
/** 0:00:01.23 -- ASS wants centiseconds and a single-digit hour. */
export function assTime(sec) {
    const s = Math.max(0, sec);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const rest = s % 60;
    const cs = Math.round((rest - Math.floor(rest)) * 100);
    // Rounding 59.999 up must not produce ":60.00".
    const secs = cs === 100 ? Math.floor(rest) + 1 : Math.floor(rest);
    return `${h}:${pad(m, 2)}:${pad(secs % 60, 2)}.${pad(cs === 100 ? 0 : cs, 2)}`;
}
export function srtTime(sec) {
    const s = Math.max(0, sec);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const secs = Math.floor(s % 60);
    const ms = Math.round((s - Math.floor(s)) * 1000);
    return `${pad(h, 2)}:${pad(m, 2)}:${pad(secs, 2)},${pad(ms === 1000 ? 999 : ms, 3)}`;
}
export function toSrt(t) {
    return t.segments
        .map((seg, i) => [
        String(i + 1),
        `${srtTime(seg.start + t.offset)} --> ${srtTime(seg.end + t.offset)}`,
        seg.text.trim(),
        '',
    ].join('\n'))
        .join('\n');
}
export function toVtt(t) {
    const body = t.segments
        .map((seg) => [
        `${srtTime(seg.start + t.offset).replace(',', '.')} --> ${srtTime(seg.end + t.offset).replace(',', '.')}`,
        seg.text.trim(),
        '',
    ].join('\n'))
        .join('\n');
    return `WEBVTT\n\n${body}`;
}
/** Break a segment into lines of at most `max` characters, on word boundaries. */
export function wrapText(text, max) {
    const words = text.trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    for (const w of words) {
        if (line.length === 0)
            line = w;
        else if (line.length + 1 + w.length <= max)
            line += ` ${w}`;
        else {
            lines.push(line);
            line = w;
        }
    }
    if (line)
        lines.push(line);
    return lines.length ? lines : [''];
}
function assColor(hex, opacity = 1) {
    // ASS is &HAABBGGRR with alpha inverted (00 = opaque).
    const r = hex.slice(1, 3);
    const g = hex.slice(3, 5);
    const b = hex.slice(5, 7);
    const a = Math.round((1 - Math.min(1, Math.max(0, opacity))) * 255)
        .toString(16)
        .padStart(2, '0');
    return `&H${a}${b}${g}${r}`.toUpperCase();
}
/**
 * Render a transcript as an ASS subtitle file. ffmpeg's `subtitles` filter
 * handles wrapping, kerning and non-latin scripts far better than chained
 * drawtext calls, so burn-in goes through this.
 */
export function toAss(t, o) {
    const fontSize = Math.round(o.fontSize * o.height);
    const marginV = Math.max(0, Math.round((1 - o.y) * o.height) - fontSize);
    const borderStyle = o.boxColor ? 3 : 1;
    // A box uses the outline slot for its padding, so a chosen outline only
    // applies to text without one -- the same as it looks in the preview.
    const outlineFraction = o.outlineColor && o.outlineWidth ? o.outlineWidth : 0.06;
    const outline = o.boxColor ? Math.round(fontSize * 0.25) : Math.max(1, Math.round(fontSize * outlineFraction));
    const outlineColour = o.boxColor ?? o.outlineColor ?? '#000000';
    const header = [
        '[Script Info]',
        'ScriptType: v4.00+',
        `PlayResX: ${o.width}`,
        `PlayResY: ${o.height}`,
        'WrapStyle: 0',
        'ScaledBorderAndShadow: yes',
        '',
        '[V4+ Styles]',
        'Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding',
        [
            'Style: Default',
            o.fontName,
            String(fontSize),
            assColor(o.color),
            assColor(o.color),
            assColor(outlineColour, o.boxColor ? o.boxOpacity : 1),
            assColor('#000000', o.shadow ? 0.6 : 0),
            o.bold ? '-1' : '0',
            '0',
            '0',
            '0',
            '100',
            '100',
            '0',
            '0',
            String(borderStyle),
            String(outline),
            o.shadow ? String(Math.max(1, Math.round(fontSize * 0.05))) : '0',
            '2',
            String(Math.round(o.width * 0.06)),
            String(Math.round(o.width * 0.06)),
            String(marginV),
            '1',
        ].join(','),
        '',
        '[Events]',
        'Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text',
    ].join('\n');
    const events = t.segments
        .filter((s) => s.end > s.start)
        .map((s) => {
        const raw = o.uppercase ? s.text.toUpperCase() : s.text;
        const text = wrapText(raw, o.maxLineLength)
            .join('\\N')
            .replace(/\{/g, '(')
            .replace(/\}/g, ')');
        return `Dialogue: 0,${assTime(s.start + t.offset)},${assTime(s.end + t.offset)},Default,,0,0,0,,${text}`;
    })
        .join('\n');
    return `${header}\n${events}\n`;
}
/** Keep only the part of a transcript that overlaps [from, to), rebased to 0. */
export function sliceTranscript(t, from, to) {
    const segments = t.segments
        .filter((s) => s.end > from && s.start < to)
        .map((s, i) => ({
        ...s,
        id: i,
        start: Math.max(0, s.start - from),
        end: Math.min(to - from, s.end - from),
        words: s.words
            .filter((w) => w.end > from && w.start < to)
            .map((w) => ({ ...w, start: Math.max(0, w.start - from), end: Math.min(to - from, w.end - from) })),
    }));
    return { language: t.language, offset: 0, segments };
}
// ---------------------------------------------------------------------------
// Untertitel hereinlesen (S21)
// ---------------------------------------------------------------------------
/**
 * Eine Zeitangabe aus SRT oder WebVTT lesen.
 *
 * Beide zählen Stunden:Minuten:Sekunden und Tausendstel, trennen sie nur
 * anders (`,` gegen `.`) -- und WebVTT darf die Stunde weglassen. Also eine
 * Rechnung für beides: was hinter dem letzten Doppelpunkt steht, sind die
 * Sekunden, davor die Minuten, davor (wenn da) die Stunden.
 *
 * `null` heisst: das ist keine Zeit. Der Leser weiter unten erkennt eine
 * Zeitzeile genau daran und nicht an ihrer Zeilennummer -- Dateien aus fremden
 * Programmen haben Kennungen, Leerzeilen und Kommentare an Stellen, an denen
 * ein Zähler danebenläge.
 */
export function leseZeit(text) {
    const treffer = text.trim().match(/^(?:(\d+):)?(\d{1,2}):(\d{1,2})(?:[.,](\d{1,3}))?$/);
    if (!treffer)
        return null;
    const [, std, min, sek, bruch] = treffer;
    const tausendstel = bruch ? Number(bruch.padEnd(3, '0')) : 0;
    return Number(std ?? 0) * 3600 + Number(min) * 60 + Number(sek) + tausendstel / 1000;
}
/**
 * Eine Untertiteldatei lesen -- SRT oder WebVTT, dasselbe Verfahren.
 *
 * Gesucht werden die Zeilen mit `-->`; alles zwischen zwei davon ist Text.
 * Damit ist es gleichgültig, ob eine Kennung davorsteht (SRT hat immer eine,
 * WebVTT darf), ob die Datei mit `WEBVTT` beginnt, und ob `NOTE`- oder
 * `STYLE`-Blöcke dazwischenliegen: was keine Zeitzeile ist und zu keinem Cue
 * gehört, fällt weg.
 *
 * Was hinter der zweiten Zeit steht (`align:start line:90%`), ist eine
 * Anweisung für die Darstellung und kein Text -- WebVTT erlaubt sie, wir
 * stellen selbst dar, also wird sie gelesen und verworfen.
 *
 * Aufgeräumt wird beim Lesen, und nur das, was nachweislich falsch wäre:
 *
 *  * Ein BOM am Anfang und CRLF-Zeilenenden -- Dateien aus Windows-Programmen
 *    haben beides, und ein BOM vor `WEBVTT` hat schon manchen Leser verwirrt.
 *  * Formatierung (`<i>`, `<b>`, `<c.gelb>`) fällt heraus: dargestellt wird
 *    hier nach dem Stilblatt des Projekts, und ein `<i>` im Text stünde sonst
 *    wörtlich im Bild.
 *  * Ein Ende vor dem Anfang wird auf den Anfang gezogen. Eine negative Dauer
 *    ist keine Meinungsfrage.
 *
 * Nicht aufgeräumt wird die Reihenfolge und nicht die Überlappung: beides
 * kann so gemeint sein, und wer eine Datei hereinlädt, will sie wiedererkennen.
 */
export function leseUntertitel(text, sprache = 'de') {
    const zeilen = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').split('\n');
    const segments = [];
    let offen = null;
    const schliessen = () => {
        if (!offen)
            return;
        const inhalt = offen.zeilen.join('\n').trim();
        if (inhalt) {
            segments.push({
                id: segments.length + 1,
                start: offen.start,
                end: Math.max(offen.start, offen.end),
                text: inhalt,
                speaker: null,
                words: [],
            });
        }
        offen = null;
    };
    for (const roh of zeilen) {
        const pfeil = roh.indexOf('-->');
        if (pfeil >= 0) {
            const von = leseZeit(roh.slice(0, pfeil));
            // Hinter der zweiten Zeit können Anweisungen stehen; genommen wird das
            // erste Stück bis zum Leerzeichen.
            const bis = leseZeit(roh.slice(pfeil + 3).trim().split(/\s+/)[0] ?? '');
            if (von !== null && bis !== null) {
                // Die Kennung einer SRT-Datei steht *vor* ihrer Zeitzeile. Liegt keine
                // Leerzeile dazwischen -- das kommt vor --, ist sie bis hierher als
                // letzte Textzeile des Cues davor gezählt worden. Eine Zeile aus
                // nichts als einer Zahl, unmittelbar vor einer Zeitzeile, ist die
                // Kennung; anderswo darf eine Zahl stehenbleiben, auch allein.
                if (offen && /^\d+$/.test(offen.zeilen.at(-1)?.trim() ?? ''))
                    offen.zeilen.pop();
                schliessen();
                offen = { start: von, end: bis, zeilen: [] };
                continue;
            }
        }
        if (!offen)
            continue;
        if (roh.trim() === '') {
            schliessen();
            continue;
        }
        // Formatierung heraus, dann die Entitäten, die WebVTT dafür braucht.
        const sauber = roh
            .replace(/<[^>]*>/g, '')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&');
        offen.zeilen.push(sauber);
    }
    schliessen();
    return Transcript.parse({ language: sprache, offset: 0, segments: segments.filter((s) => s.text) });
}
//# sourceMappingURL=transcript.js.map